import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@sea-snag/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Layers, Loader2, MoreVertical, Plus, Ship } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { CompSetWizardDialog } from '../../components/comp-set-wizard';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { LoadingState } from '../../components/loading-state';
import { useRequiredOrganization } from '../../hooks/use-required-organization';
import { type CompSet, compSetsApi } from '../../lib/api-client';
import { maxMToFt, minMToFt } from '../../lib/length-filter';
import { queryKeys } from '../../lib/query-keys';

export const Route = createFileRoute('/$orgPid/competitor-sets/')({
  component: CompetitorSetsPage,
});

function CompetitorSetsPage() {
  const { organization, isLoading } = useRequiredOrganization();

  if (isLoading) return <LoadingState />;

  return <CompetitorSetsContent orgPid={organization.pid} />;
}

function CompetitorSetsContent({ orgPid }: { orgPid: string }) {
  const [deleteTarget, setDeleteTarget] = useState<CompSet | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.compSets.list(orgPid),
    queryFn: () => compSetsApi.list({ limit: 100 }),
  });

  const deleteMutation = useMutation({
    mutationFn: (pid: string) => compSetsApi.remove(pid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.compSets.list(orgPid) });
      toast.success('Competitor set deleted');
    },
    onError: () => toast.error('Failed to delete competitor set'),
  });

  const compSets = data?.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Competitor Sets</h1>
          <p className="text-muted-foreground">Group similar boats to compare pricing</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 size-4" />
          Create Set
        </Button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      ) : compSets.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Layers className="mx-auto mb-2 size-8 opacity-50 text-muted-foreground" />
            <p className="text-muted-foreground">No competitor sets yet.</p>
            <p className="text-muted-foreground text-xs">
              Create a set to start comparing your pricing against competitors.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {compSets.map((cs) => (
            <Link
              key={cs.id}
              to="/$orgPid/competitor-sets/$pid"
              params={{ orgPid, pid: cs.id }}
              className="block"
            >
              <Card className="cursor-pointer transition-shadow hover:shadow-md h-full">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{cs.name}</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="size-8 p-0"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDeleteTarget(cs);
                      }}
                    >
                      <MoreVertical className="size-4" />
                    </Button>
                  </div>
                  {cs.referenceBoats.length > 0 && (
                    <CardDescription className="flex items-center gap-1 text-xs">
                      <Ship className="size-3" />
                      {cs.referenceBoats.map((b) => b.name).join(', ')}
                    </CardDescription>
                  )}
                  {cs.description && cs.referenceBoats.length === 0 && (
                    <CardDescription className="text-xs">{cs.description}</CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2">
                    {cs.status === 'syncing' ? (
                      <Badge variant="secondary" className="animate-pulse">
                        <Loader2 className="mr-1 size-3 animate-spin" />
                        Syncing...
                      </Badge>
                    ) : cs.status === 'failed' ? (
                      <Badge variant="destructive">Sync failed</Badge>
                    ) : (
                      <Badge variant="secondary">
                        {cs.marketBoatCount}{' '}
                        {cs.marketBoatCount === 1 ? 'competitor' : 'competitors'}
                      </Badge>
                    )}
                    {cs.smartSyncEnabled && (
                      <Badge variant="outline" className="text-xs">
                        Smart Sync
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {cs.filterKind && (
                      <Badge variant="outline" className="text-xs">
                        {cs.filterKind}
                      </Badge>
                    )}
                    {(cs.filterMinLength != null || cs.filterMaxLength != null) && (
                      <Badge variant="outline" className="text-xs">
                        {`${cs.filterMinLength != null ? minMToFt(Number(cs.filterMinLength)) : '?'}–${
                          cs.filterMaxLength != null ? maxMToFt(Number(cs.filterMaxLength)) : '?'
                        } ft`}
                      </Badge>
                    )}
                    {cs.filterHomeBases.length > 0 && (
                      <Badge variant="outline" className="text-xs">
                        {cs.filterHomeBases.length === 1
                          ? cs.filterHomeBases[0]
                          : `${cs.filterHomeBases.length} bases`}
                      </Badge>
                    )}
                    {cs.filterCharterNames.length > 0 && (
                      <Badge variant="outline" className="text-xs">
                        {cs.filterCharterNames.length} charters
                      </Badge>
                    )}
                    {(cs.filterMinCabins != null || cs.filterMaxCabins != null) && (
                      <Badge variant="outline" className="text-xs">
                        {cs.filterMinCabins ?? '?'}–{cs.filterMaxCabins ?? '?'} cab
                      </Badge>
                    )}
                  </div>
                  <Button variant="outline" size="sm" className="w-full">
                    View Details
                  </Button>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Competitor Set"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => {
          if (deleteTarget) {
            deleteMutation.mutate(deleteTarget.id);
            setDeleteTarget(null);
          }
        }}
      />

      <CompSetWizardDialog
        mode="create"
        orgPid={orgPid}
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
    </div>
  );
}
